import { useState } from "react";
import { useCurrency } from "../lib/useCurrency";
import { Link } from "react-router-dom";
import { SiteLogo } from "../components/marketing/SiteLogo";
import { Reveal } from "../components/marketing/Reveal";
import { Industries } from "../components/marketing/Industries";
import { LanguageMenu } from "../components/LanguageMenu";
import { HeroDemo } from "../components/marketing/HeroDemo";
import { WhatItDoes, DayWithOctoMail, YouStayInControl } from "../components/marketing/Sections";
import { CheckIcon } from "../components/icons";
import {
  PRICE_DISPLAY,
  type Currency,
} from "../lib/currency";
import { useAuth } from "../contexts/auth-context";
import { useT, useLocale, type Locale } from "../i18n/locale-context";
import { persistLocale } from "../i18n";

/** Static landing page per language (prerendered as /, /uz, /ru). */
const LOCALE_PATH: Record<Locale, string> = { en: "/", uz: "/uz", ru: "/ru" };

/**
 * Header: the globe language menu. Its items are plain links to the
 * prerendered landing pages (not a redirect by browser language), so search
 * engines see all three and nobody is bounced by a device setting. Picking one
 * also stores the choice so /login and the app follow it.
 */
function HeaderLanguage() {
  const { locale, strings } = useLocale();
  return (
    <LanguageMenu
      current={locale}
      label={strings.nav.language}
      hrefFor={(l) => LOCALE_PATH[l]}
      onSelect={(l) => persistLocale(l)}
      compact="lg"
    />
  );
}

/** Footer: the same three links, spelled out in their own language. */
function FooterLanguages() {
  const { locale } = useLocale();
  return (
    <div className="flex items-center gap-3 text-sm">
      {(["en", "uz", "ru"] as const).map((l) => (
        <a
          key={l}
          href={LOCALE_PATH[l]}
          hrefLang={l}
          lang={l}
          aria-current={l === locale ? "page" : undefined}
          onClick={() => persistLocale(l)}
          className={l === locale ? "font-semibold text-white" : "text-slate-400 hover:text-white"}
          // Off the Russian page, "Русский" uses the system font so English and
          // Uzbek visitors don't download the Cyrillic web font (PageSpeed).
          style={l === "ru" && locale !== "ru" ? { fontFamily: "system-ui, -apple-system, sans-serif" } : undefined}
        >
          {l === "en" ? "English" : l === "uz" ? "Oʻzbekcha" : "Русский"}
        </a>
      ))}
    </div>
  );
}

/**
 * OctoMail marketing landing page.
 * Aesthetic: the OctoAIverse family (octoaiverse.com): a navy header and hero
 * with dark glass, clean sans headlines, OctoMail sky blue as the action
 * colour, crisp light sections below. Website only: `.site` scopes the fonts.
 * Audience: small businesses that run on inbound inquiries — contractors,
 * dealerships, dental offices, auto service, RV stores, sales teams.
 */
export function Landing() {
  return (
    <div className="site min-h-screen overflow-x-clip">
      <SiteHeader />
      <Hero />
      <WhatItDoes />
      <DayWithOctoMail />
      <YouStayInControl />
      <Industries />
      <StatsBand />
      <Pricing />
      <FAQ />
      <SiteFooter />
    </div>
  );
}

/* ---------------------------------- Header --------------------------------- */

function SiteHeader() {
  const { user, loading } = useAuth();
  const t = useT();
  return (
    <header className="glass-header sticky top-0 z-30">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
        <div className="flex items-center gap-10 min-[860px]:gap-7 lg:gap-12">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2"
        >
          <SiteLogo tone="light" />
        </a>
        <nav className="hidden items-center gap-5 text-sm font-medium text-slate-600 min-[860px]:flex lg:gap-7">
          <a href="#see-it-work" className="hover:text-slate-900">{t.nav.how}</a>
          <a href="#industries" className="hover:text-slate-900">{t.nav.industries}</a>
          <a href="#pricing" className="hover:text-slate-900">{t.nav.pricing}</a>
          <a href="#faq" className="hover:text-slate-900">{t.nav.faq}</a>
        </nav>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <HeaderLanguage />
          {loading ? null : user ? (
            <Link to="/app" className="btn-pill text-sm">
              {t.nav.openApp}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="btn-pill-outline hidden text-sm sm:inline-flex"
              >
                {t.nav.signIn}
              </Link>
              <Link to="/login" className="btn-pill text-sm">
                {t.nav.startFree}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ----------------------------------- Hero ---------------------------------- */

function Hero() {
  const t = useT();
  const h = t.landing.hero;
  return (
    <section id="top" className="bg-navy relative overflow-hidden text-white">
      <div className="bg-navy-dots pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 sm:px-8 sm:pt-20 min-[860px]:grid-cols-[1.05fr_1fr] min-[860px]:gap-10 min-[860px]:pt-14 lg:gap-14 lg:pb-24 lg:pt-20">
        <div className="text-center min-[860px]:text-left">
          <p className="animate-rise text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">{h.eyebrow}</p>
          {/* The headline and lead are the LCP candidates: they paint at once,
              with no fade-in, so the largest text is visible on first paint.
              The eyebrow, CTAs and demo card keep the staggered rise. */}
          <h1 className="font-display mt-4 text-[2.6rem] font-semibold leading-[1.04] text-white sm:text-[3.8rem] min-[860px]:text-[3.1rem] lg:text-[3.8rem]">
            {h.h1}
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-300 min-[860px]:mx-0">
            {h.lead}
          </p>
          <div className="animate-rise mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center min-[860px]:justify-start" style={{ animationDelay: "180ms" }}>
            <Link to="/login" className="btn-pill w-full max-w-sm text-base sm:w-auto">{h.ctaTrial}</Link>
            <a href="#see-it-work" className="btn-pill-dark w-full max-w-sm text-base sm:w-auto">{h.ctaSee}</a>
          </div>
          <p className="animate-rise mt-3 text-sm text-slate-400" style={{ animationDelay: "220ms" }}>{h.fine}</p>
        </div>
        <div className="animate-rise mx-auto w-full max-w-md min-[860px]:max-w-none" style={{ animationDelay: "260ms" }}>
          <HeroDemo />
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- Stats band ------------------------------- */

function StatsBand() {
  const t = useT();
  const stats = t.landing.stats.items;
  return (
    <section className="px-5 py-8 sm:px-8 sm:py-10">
      <div className="panel-navy mx-auto max-w-6xl px-5 py-14 sm:px-10 sm:py-16">
        <Reveal>
          <p className="text-center text-sm font-semibold uppercase tracking-wide text-sky-300">
            {t.landing.stats.eyebrow}
          </p>
          <h2 className="font-display mx-auto mt-3 max-w-2xl text-center text-[1.6rem] font-semibold leading-[1.1] text-white sm:text-[2.1rem]">
            {t.landing.stats.title}
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {stats.map((s, i) => (
            <Reveal key={s.big} delay={i * 90}>
              <div className="text-center">
                <div className="font-display text-5xl font-semibold text-white">
                  {s.big}
                </div>
                <p className="mx-auto mt-2 max-w-[15rem] text-sm leading-relaxed text-slate-300">
                  {s.small}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <p className="mt-10 text-center text-xs text-slate-400">
            {t.landing.stats.note}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------- Pricing --------------------------------- */

function Pricing() {
  const t = useT();
  const pr = t.landing.pricing;
  const currency = useCurrency(true);
  const [annual, setAnnual] = useState(false);
  const p = PRICE_DISPLAY[currency];
  const priceMain = annual ? p.annualPerMonth : p.monthly;
  const billedNote = annual ? pr.billedYearly(p.annual, p.code) : pr.billedMonthly(p.code);

  // Derive day-rate from the displayed price string (strip non-numeric except dot).
  const monthlyNumeric = parseFloat(p.monthly.replace(/[^0-9.]/g, ""));
  const dayRate = (monthlyNumeric / 30).toFixed(2);
  const currencySymbol = currency === "cad" ? "CA$" : "$";
  const dayRateStr = `${currencySymbol}${dayRate}`;

  return (
    <section id="pricing" className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-glows" />
      <div className="relative mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
        <Reveal>
          <SectionHeading
            eyebrow={pr.eyebrow}
            title={pr.title}
            sub={pr.sub}
          />
        </Reveal>
        <Reveal delay={100}>
          <div className="mx-auto mt-4 max-w-md">
            {/* billing-cycle toggle (currency is auto-detected: CAD in Canada, USD elsewhere) */}
            <div className="mb-4 flex items-center justify-center">
              <div className="glass-toggle inline-flex rounded-full p-1 ring-1 ring-slate-200">
                {(["monthly", "annual"] as const).map((mode) => {
                  const on = (mode === "annual") === annual;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setAnnual(mode === "annual")}
                      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                        on ? "bg-[#0e1726] text-white" : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      {mode === "monthly" ? pr.monthly : pr.annual}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="glass-strong p-4 text-center sm:p-5">
              <h3 className="text-lg font-semibold text-slate-900">{pr.planName}</h3>
              <p className="mt-0.5 text-sm text-slate-600">{pr.planSub}</p>
              <div className="mt-3 flex items-baseline justify-center gap-1.5">
                <span className="font-display text-4xl font-semibold text-slate-900">
                  {priceMain}
                </span>
                <span className="text-slate-600">{pr.perMo(p.code)}</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {billedNote}
                {annual ? "" : pr.switchNote}
              </p>

              {/* Price-justification facts */}
              <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2">
                {[pr.factDay(dayRateStr), pr.factAdmin, pr.factPays].map((fact) => (
                  <span
                    key={fact}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200"
                  >
                    {fact}
                  </span>
                ))}
              </div>

              <ul className="mx-auto mt-3 max-w-xs space-y-1.5 text-left text-sm text-slate-700">
                {pr.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/login" className="btn-pill mt-4 w-full text-base">
                {pr.cta}
              </Link>
              <p className="mt-2 text-xs text-slate-600">{pr.fine}</p>
              <p className="mt-1 text-xs text-slate-600">{pr.telegramNote}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------------- FAQ ----------------------------------- */

function FAQ() {
  const t = useT();
  const qa = t.landing.faq.items;
  return (
    <section id="faq" className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
      <Reveal>
        <SectionHeading eyebrow={t.landing.faq.eyebrow} title={t.landing.faq.title} />
      </Reveal>
      <Reveal delay={80}>
        <div className="glass-card mt-10 divide-y divide-slate-200/80 overflow-hidden">
          {qa.map((item) => (
            <details key={item.q} className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 [&::-webkit-details-marker]:hidden">
                <span className="font-medium text-slate-900">{item.q}</span>
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-open:rotate-180"
                  aria-hidden="true"
                >
                  <path d="M5 8l5 5 5-5" />
                </svg>
              </summary>
              <div className="px-6 pb-5 pt-0">
                <p className="leading-relaxed text-slate-600">{item.a}</p>
              </div>
            </details>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

/* --------------------------------- Footer ---------------------------------- */

/** OctoAIverse social set. Keep identical to the JSON-LD sameAs list. */
const FOOTER_SOCIALS: { label: string; href: string; icon: JSX.Element }[] = [
  {
    label: "X",
    href: "https://x.com/OctoAIverse",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </svg>
    ),
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/octoaiverse/",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="2.5" y="2.5" width="19" height="19" rx="5" />
        <circle cx="12" cy="12" r="4.3" />
        <circle cx="17.6" cy="6.4" r="1.15" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@OctoAIverse",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@octoaiverse",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
      </svg>
    ),
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/octoaiverse/",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
      </svg>
    ),
  },
];

function SiteFooter() {
  const t = useT();
  const f = t.landing.footer;
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0e1726] text-slate-300">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-sm">
            <SiteLogo tone="dark" />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {f.tagline}
            </p>
            <div className="mt-5 flex items-center gap-3">
              {FOOTER_SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={f.socialAria(s.label)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-slate-400 transition-colors hover:border-white/50 hover:text-white [&>svg]:h-4 [&>svg]:w-4"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-12 gap-y-8 text-sm">
            <div>
              <p className="font-semibold text-white">{f.product}</p>
              <ul className="mt-3 space-y-2 text-slate-400">
                <li><a href="#see-it-work" className="hover:text-white">{t.nav.how}</a></li>
                <li><a href="#industries" className="hover:text-white">{t.nav.industries}</a></li>
                <li><a href="/pricing" className="hover:text-white">{t.nav.pricing}</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-white">{f.industries}</p>
              <ul className="mt-3 space-y-2 text-slate-400">
                <li><a href="/for/contractors" className="hover:text-white">{f.links.contractors}</a></li>
                <li><a href="/for/car-dealerships" className="hover:text-white">{f.links.dealerships}</a></li>
                <li><a href="/for/dentists" className="hover:text-white">{f.links.dental}</a></li>
                <li><a href="/for/auto-repair" className="hover:text-white">{f.links.auto}</a></li>
                <li><a href="/for/rv-dealers" className="hover:text-white">{f.links.rv}</a></li>
                <li><a href="/for/sales-teams" className="hover:text-white">{f.links.sales}</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-white">{f.answers}</p>
              <ul className="mt-3 space-y-2 text-slate-400">
                <li><a href="/answers/what-is-speed-to-lead" className="hover:text-white">{f.links.speedToLead}</a></li>
                <li><a href="/answers/how-fast-should-you-respond-to-leads" className="hover:text-white">{f.links.howFast}</a></li>
                <li><a href="/answers/octomail-vs-hiring-a-receptionist" className="hover:text-white">{f.links.vsReceptionist}</a></li>
                <li><a href="/answers/can-ai-reply-to-business-emails" className="hover:text-white">{f.links.canAi}</a></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-white">{f.company}</p>
              <ul className="mt-3 space-y-2 text-slate-400">
                <li><Link to="/login" className="hover:text-white">{t.nav.signIn}</Link></li>
                <li><a href="mailto:support@octoaiverse.com" className="hover:text-white">{f.links.contact}</a></li>
                <li><a href="/privacy" className="hover:text-white">{f.links.privacy}</a></li>
                <li><a href="/terms" className="hover:text-white">{f.links.terms}</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start gap-3 border-t border-white/10 pt-6 text-sm text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          {/* The year is baked in at build time; don't fight it over New Year's. */}
          <p suppressHydrationWarning>
            {f.copyrightA(year)}
            <a
              href="https://octoaiverse.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white"
            >
              OctoAIverse
            </a>
            {f.copyrightB}
          </p>
          <FooterLanguages />
          <a
            href="https://octoaiverse.com"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-white hover:text-brand-300"
          >
            OctoAIverse
          </a>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------- Shared bits ------------------------------ */

function SectionHeading({
  eyebrow,
  title,
  sub,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
        {eyebrow}
      </p>
      <h2 className="font-display mt-3 text-[1.75rem] font-semibold leading-[1.05] text-slate-900 sm:text-[2.5rem]">
        {title}
      </h2>
      {sub && <p className="mt-4 text-lg leading-relaxed text-slate-600">{sub}</p>}
    </div>
  );
}
